import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AppShell from "../../components/layout/AppShell";
import StepProgressBar from "../../components/ui/StepProgressBar";
import { listReceivers, createQuote, createTransaction, getVaultBalance, getBanks } from "../../services/api";
import { useSelector } from "react-redux";

const STEPS = ["Create Now", "Review", "Complete"];

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtUsdc = (n) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 7, maximumFractionDigits: 7 });

const fmtFiat = (n) => {
  const floored = Math.floor(Number(n ?? 0) * 100) / 100;
  return floored.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CreateTransaction = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(0);

  // ── Receivers & wallet ────────────────────────────────────────────────────
  const [receivers, setReceivers]         = useState([]);
  const [rcvLoading, setRcvLoading]       = useState(true);
  const [walletBalance, setWalletBalance] = useState("0");

  // ── Form fields ───────────────────────────────────────────────────────────
  const [selectedReceiverId, setSelectedReceiverId] = useState("");
  const [usdAmount, setUsdAmount]                   = useState("");
  const [description, setDescription]               = useState("");
  const [errors, setErrors]                         = useState({});

  // ── Seed quote (rate) ─────────────────────────────────────────────────────
  const [seedRate, setSeedRate]       = useState(0);   // USD received per 1 USDC
  const [rateLoading, setRateLoading] = useState(true);
  const [rateError, setRateError]     = useState(false);

  // ── Receiver bank (fetched when receiver is selected) ─────────────────────
  const [receiverBankId, setReceiverBankId] = useState(null);

  // ── Confirm step ──────────────────────────────────────────────────────────
  const [confirming, setConfirming] = useState(false);
  const [txnResult, setTxnResult]   = useState(null);  // { id, ... } from createTransaction

  // ── Confirmed USDC amount (set from locked quote on confirm) ─────────────
  const [confirmedUsdc, setConfirmedUsdc] = useState(0);

  // ── Derived ───────────────────────────────────────────────────────────────
  const walletAmt    = Number(walletBalance);
  const usdVal       = Number(usdAmount) || 0;
  // USDC sender pays = USD amount / seedRate  (seedRate = USD per USDC = 1/fromAmtPerDollar)
  const usdcRequired = seedRate > 0 ? usdVal / seedRate : 0;
  const afterPayment = Math.max(0, walletAmt - usdcRequired);
  const selectedReceiver = receivers.find((r) => r.id === selectedReceiverId) || null;

  // ── Load receivers, wallet, seed quote ───────────────────────────────────
  // Seed quote requests $1 USD (toAmount) — the API returns the USDC fromAmount
  // needed for $1. seedRate = 1/fromAmt (USD per USDC) keeps existing display logic intact.
  const fetchSeedQuote = useCallback(async (senderUserId) => {
    if (!senderUserId) return;
    setRateLoading(true);
    setRateError(false);
    try {
      const res = await createQuote({
        userId:       senderUserId,
        fromCurrency: "USDC",
        toCurrency:   "USD",
        toAmount:     "1",          // $1 USD target → fromAmount = USDC per $1
        category:     "OFFRAMP",
      });
      const q       = res.data;
      const fromAmt = Number(q?.fromAmount ?? q?.source_amount ?? 0); // USDC per $1
      // seedRate = USD per USDC — keeps rate label and usdcRequired formula unchanged
      const rate = fromAmt > 0
        ? 1 / fromAmt
        : Number(q?.rate ?? q?.forex_rate ?? 0);
      setSeedRate(rate);
    } catch {
      setRateError(true);
    } finally {
      setRateLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    Promise.all([listReceivers(), getVaultBalance(userId)])
      .then(([rcvRes, vaultRes]) => {
        const list = rcvRes?.data ?? rcvRes ?? [];
        setReceivers(Array.isArray(list) ? list : []);
        const usdc = vaultRes?.data?.balance?.find((b) => b.symbol === "USDC");
        setWalletBalance(usdc?.balance ?? "0");
      })
      .catch(() => {})
      .finally(() => setRcvLoading(false));

    fetchSeedQuote(userId);
  }, [userId, fetchSeedQuote]);

  // Fetch receiver's bank whenever selection changes
  useEffect(() => {
    if (!selectedReceiverId) { setReceiverBankId(null); return; }
    getBanks(selectedReceiverId)
      .then((r) => {
        const banks = r?.data?.banks ?? [];
        const approved = banks.find((b) => b.status === "APPROVE");
        setReceiverBankId(approved?.bankId ?? null);
      })
      .catch(() => setReceiverBankId(null));
  }, [selectedReceiverId]);

  // ── Step 0 → Review ───────────────────────────────────────────────────────
  const handleCreateTransaction = () => {
    const errs = {};
    if (!selectedReceiverId) errs.receiver  = "Please select a receiver.";
    if (!usdAmount || usdVal <= 0) errs.amount = "Enter a valid USD amount greater than 0.";
    if (usdcRequired > walletAmt) errs.balance = "Insufficient wallet balance.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep(1);
  };

  // ── Step 1 → Complete ─────────────────────────────────────────────────────
  const handleConfirmAndPay = async () => {
    if (!receiverBankId) {
      toast.error("Receiver has no approved bank account. Ask them to add one.");
      return;
    }
    setConfirming(true);
    try {
      // Lock fresh quote using toAmount (exact USD target) — API returns the
      // precise USDC fromAmount, avoiding rounding from back-calculation.
      const quoteRes = await createQuote({
        userId:       userId,
        fromCurrency: "USDC",
        toCurrency:   "USD",
        toAmount:     String(usdVal),  // exact USD the receiver gets
        category:     "OFFRAMP",
      });
      const lockedQuote = quoteRes.data;

      // Capture the locked fromAmount for the success screen
      const lockedFrom = Number(lockedQuote?.fromAmount ?? lockedQuote?.source_amount ?? 0);
      if (lockedFrom > 0) setConfirmedUsdc(lockedFrom);

      // Execute transaction
      const txnRes  = await createTransaction({
        quoteId:       lockedQuote?.id,
        bankAccountId: receiverBankId,
      });
      const txn = txnRes?.data ?? txnRes;
      setTxnResult(txn);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const rateLabel = seedRate > 0
    ? `ⓢ 1.00 USDC = $${fmtFiat(seedRate)} USD`
    : null;

  return (
    <AppShell>
      <div className="max-w-[560px]">
        <div className="mb-8">
          <StepProgressBar steps={STEPS} currentStep={step} />
        </div>

        {/* ── Step 0: Create Now ────────────────────────────────────────────── */}
        {step === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Create Transaction</h2>
            <p className="text-sm text-gray-400 mb-6">
              Enter amount for your mapped receivers.
            </p>

            {/* USD Amount */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Enter USD amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={usdAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "" && !/^\d*\.?\d{0,2}$/.test(val)) return;
                    setUsdAmount(val);
                    setErrors((p) => ({ ...p, amount: "", balance: "" }));
                  }}
                  placeholder="0.00"
                  className={`w-full border rounded-xl pl-8 pr-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                    errors.amount || errors.balance ? "border-red-400" : "border-gray-200"
                  }`}
                />
              </div>
              {errors.amount && <p className="mt-1 text-xs text-red-500">{errors.amount}</p>}
            </div>

            {/* Receiver */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Receiver</label>
              {rcvLoading ? (
                <div className="h-11 bg-gray-100 rounded-xl animate-pulse" />
              ) : (
                <select
                  value={selectedReceiverId}
                  onChange={(e) => {
                    setSelectedReceiverId(e.target.value);
                    setErrors((p) => ({ ...p, receiver: "" }));
                  }}
                  className={`w-full border rounded-xl px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${
                    errors.receiver
                      ? "border-red-400 text-gray-400"
                      : selectedReceiverId
                      ? "border-gray-200 text-gray-900"
                      : "border-gray-200 text-gray-400"
                  }`}
                >
                  <option value="">Select receiver...</option>
                  {receivers.map((r) => (
                    <option key={r.id} value={r.id}>{r.name || r.email || r.id}</option>
                  ))}
                </select>
              )}
              {errors.receiver && <p className="mt-1 text-xs text-red-500">{errors.receiver}</p>}
            </div>

            {/* Description */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description (optional)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              />
            </div>

            {/* Conversion rate */}
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-3">
              <span className="text-sm text-gray-500">Conversion Rate</span>
              {rateLoading ? (
                <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
              ) : rateError ? (
                <button
                  onClick={() => fetchSeedQuote(userId)}
                  className="text-xs text-brand hover:underline"
                >
                  Retry
                </button>
              ) : (
                <span className="text-sm font-semibold text-gray-900">{rateLabel}</span>
              )}
            </div>

            {/* Wallet balance */}
            <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 mb-1">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Wallet Balance</p>
                <p className="text-sm font-semibold text-gray-900">
                  {fmtUsdc(walletBalance)} USDC
                </p>
              </div>
              <button
                onClick={() => navigate("/sender/treasury/fund")}
                className="text-sm font-medium text-brand border border-brand rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors flex-shrink-0"
              >
                Fund Wallet
              </button>
            </div>

            {/* After-payment preview */}
            {usdcRequired > 0 && !errors.balance && usdcRequired <= walletAmt && (
              <p className="text-xs text-gray-400 mb-5 mt-1 ml-1">
                After payment: {fmtUsdc(afterPayment)} USDC remaining
              </p>
            )}
            {errors.balance && (
              <p className="text-xs text-red-500 mb-5 mt-1 ml-1">{errors.balance}</p>
            )}
            {!(usdcRequired > 0) && <div className="mb-5" />}

            <button
              onClick={handleCreateTransaction}
              disabled={rateLoading || rateError}
              className="w-full bg-brand text-white text-sm font-semibold py-3 rounded-lg hover:bg-brandHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Transaction
            </button>
          </div>
        )}

        {/* ── Step 1: Review ────────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Review Transaction</h2>
            <p className="text-sm text-gray-400 mb-6">
              Once confirmed, the transaction will proceed.
            </p>

            <div className="border border-gray-100 rounded-xl overflow-hidden mb-6">
              {/* Transaction Amount */}
              <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
                <span className="text-sm text-gray-500 w-36 flex-shrink-0">Transaction Amount</span>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 flex items-center justify-end gap-1">
                    <span className="text-gray-400">ⓢ</span>
                    {fmtUsdc(usdcRequired)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    After payment: {fmtUsdc(afterPayment)} USDC remaining
                  </p>
                </div>
              </div>

              {/* Receiver */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <span className="text-sm text-gray-500">Receiver</span>
                <span className="text-sm font-semibold text-gray-900">
                  {selectedReceiver?.name || selectedReceiver?.email || "—"}
                </span>
              </div>

              {/* Description */}
              <div className="flex items-center justify-between px-5 py-4">
                <span className="text-sm text-gray-500">Description</span>
                <span className="text-sm font-semibold text-gray-900">
                  {description || <span className="text-gray-400 font-normal italic">None</span>}
                </span>
              </div>
            </div>

            <button
              onClick={handleConfirmAndPay}
              disabled={confirming}
              className="w-full bg-brand text-white text-sm font-semibold py-3 rounded-lg hover:bg-brandHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirming ? "Processing…" : "Confirm and Pay"}
            </button>
          </div>
        )}

        {/* ── Step 2: Complete ──────────────────────────────────────────────── */}
        {step === 2 && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center text-center">
            {/* Icon */}
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-pink-400" />
              <span className="absolute top-0 -left-3 w-1.5 h-1.5 rounded-full bg-yellow-400" />
              <span className="absolute -bottom-1 right-0 w-1.5 h-1.5 rounded-full bg-orange-400" />
              <span className="absolute -bottom-2 -left-1 w-1 h-1 rounded-full bg-pink-300" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Complete!</h2>
            <p className="text-sm text-gray-500 mb-8">
              Your payment has been sent and the receiver has been notified.
            </p>

            {/* Summary */}
            <div className="w-full border border-gray-100 rounded-xl overflow-hidden mb-8 text-left">
              {[
                ["Amount",         `ⓢ ${fmtUsdc(confirmedUsdc || usdcRequired)}`],
                ["Transaction ID", txnResult?.id ?? "—"],
                ["Receiver",       selectedReceiver?.name || selectedReceiver?.email || "—"],
                ["Description",    description || "—"],
              ].map(([label, value], i, arr) => (
                <div
                  key={label}
                  className={`flex items-center justify-between px-5 py-3 ${
                    i < arr.length - 1 ? "border-b border-gray-100" : ""
                  }`}
                >
                  <span className="text-sm text-gray-400">{label}</span>
                  <span className="text-sm font-medium text-gray-900 text-right break-all ml-4">{value}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate("/sender/transactions")}
              className="px-8 py-2.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brandHover transition-colors"
            >
              Back to Transactions
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default CreateTransaction;
